import { useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { Form, Formik, FormikConsumer } from 'formik';

import {
  LinkConfigurationStepConfirmation,
  Session,
  UnmoderatedTaskLink,
  UnmoderatedTaskSetting,
  useIsAdmin,
  usePlan,
  useTaskSetting,
  useUpdateUnmoderatedTaskSetting,
} from '@app/api';
import { useMarkStudyStepAsValidated } from 'queries';
import { InformationBox, TextArea, useBoolModalState } from 'components';

import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionFormSection } from './components/SessionFormSection';
import { useFormikSubmit } from '../../../hooks/useFormikSubmit';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import { useRedirectIfDraft } from './hooks/useRedirectIfDraft';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { RecordingCompletion } from './components/RecordingCompletion';
import { MatchingParticipants } from './components/MatchingParticipants';
import {
  DEFAULT_STUDY_TOOLS_LINKS,
  UnmoderatedTaskLinkInputs,
} from './components/UnmoderatedTaskLinkInputs';
import { useTranslation } from 'react-i18next';
import { getMaxNumberOfQuestions } from 'lib/billing';
import { UpsellTargetingModal } from 'components/Features/UpsellTargetingModal';

const useStyles = createUseStyles((theme: Theme) => ({
  box: {
    marginTop: theme.marginBase * 3,
  },
}));

interface Props {
  session: Session;
}

interface Values {
  studyToolLinks: UnmoderatedTaskLink[];
  studyToolInstructions?: string;
  linkConfigurationStepConfirmation: LinkConfigurationStepConfirmation[];
  areLinksEditable?: boolean;
}

export const EditSessionLinkConfiguration = ({ session }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [upsellModalOpened, openUpsellModal, closeUpsellModal] =
    useBoolModalState();
  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );
  const { data: unmoderatedTaskSetting } =
    useTaskSetting<UnmoderatedTaskSetting>(session.taskSettingId);
  const { data: plan } = usePlan(session?.planId);
  const maxQuestions = getMaxNumberOfQuestions(plan?.products);
  const isAdmin = useIsAdmin();

  const { mutateAsync: updateUnmoderatedTaskSetting } =
    useUpdateUnmoderatedTaskSetting(unmoderatedTaskSetting?.id);

  const update = useCallback(
    async (values: Values) => {
      if (!unmoderatedTaskSetting) {
        throw Error('Unmoderated task setting not found');
      }

      const links = values.studyToolLinks?.map((link) => ({
        ...link,
        studyToolLink: link.studyToolLink.trim(),
      }));
      const ts = await updateUnmoderatedTaskSetting({
        studyToolLinks: links || DEFAULT_STUDY_TOOLS_LINKS,
        studyToolInstructions: values.studyToolInstructions || '',
        linkConfigurationStepConfirmation:
          values.linkConfigurationStepConfirmation || [],
        areLinksEditable: values.areLinksEditable,
      });
      await markStepAsValidated({ step: 'link-configuration' });
      return {
        studyToolLinks: ts.studyToolLinks || DEFAULT_STUDY_TOOLS_LINKS,
        studyToolInstructions: ts.studyToolInstructions || '',
        linkConfigurationStepConfirmation:
          ts.linkConfigurationStepConfirmation || [],
        areLinksEditable: ts.areLinksEditable,
      };
    },
    [markStepAsValidated, unmoderatedTaskSetting, updateUnmoderatedTaskSetting],
  );

  const onSuccess = useRedirectIfDraft(session);
  const initialEntity = useMemo(() => {
    return {
      studyToolLinks: unmoderatedTaskSetting?.studyToolLinks.length
        ? unmoderatedTaskSetting?.studyToolLinks
        : DEFAULT_STUDY_TOOLS_LINKS,
      studyToolInstructions:
        unmoderatedTaskSetting?.studyToolInstructions || '',
      linkConfigurationStepConfirmation:
        unmoderatedTaskSetting?.linkConfigurationStepConfirmation || [],
      areLinksEditable: unmoderatedTaskSetting?.areLinksEditable,
    };
  }, [session, unmoderatedTaskSetting]);

  const [initialValues, submit] = useFormikSubmit(
    update,
    initialEntity,
    undefined,
    { onSuccess },
  );

  const settingsValidationSchema = Yup.object().shape({
    studyToolLinks: Yup.array()
      .of(
        Yup.object().shape({
          studyToolLink: Yup.string().required(),
        }),
      )
      .min(1)
      .required(),
    studyToolInstructions: Yup.string().required(),
    linkConfigurationStepConfirmation: Yup.array()
      .of(Yup.string().oneOf(Object.values(LinkConfigurationStepConfirmation)))
      .min(2)
      .max(2)
      .required(),
  });

  if (!unmoderatedTaskSetting) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={settingsValidationSchema}
    >
      <Form>
        <EditSessionLayout
          session={session}
          footer={
            <FormikConsumer>
              {({ values }) => (
                <SessionSubmitLine
                  session={session}
                  duration={values.duration}
                />
              )}
            </FormikConsumer>
          }
        >
          <SessionFormSection
            title={t(
              'editSession.linkConfiguration.participantInstructions.title',
            )}
          >
            <InformationBox
              text={t(
                'editSession.linkConfiguration.participantInstructions.help',
                { context: session.studyType },
              )}
              variant="info"
              className={classes.box}
            />
            <UnmoderatedTaskLinkInputs
              name="studyToolLinks"
              linkEditableName="areLinksEditable"
              session={session}
            />
            <TextArea
              name="studyToolInstructions"
              label={t('sessions.display.linkInformations')}
              placeholder={t(
                'editSession.linkConfiguration.participantInstructions.placeholder',
              )}
            />
          </SessionFormSection>
          <SessionFormSection title={t('editSession.linkConfiguration.title')}>
            <RecordingCompletion
              taskSettingId={session.taskSettingId}
              showUpsell={maxQuestions === 0 && !isAdmin}
              openUpsellModal={openUpsellModal}
            />
            <MatchingParticipants />
          </SessionFormSection>
        </EditSessionLayout>
        <UpsellTargetingModal
          title={t(
            'editSession.linkConfiguration.studyLinkConfiguration.advancedUpsellTitle',
          )}
          isOpen={upsellModalOpened}
          onRequestClose={closeUpsellModal}
          sessionId={session.id}
        />
      </Form>
    </Formik>
  );
};
